import { useState, useEffect } from 'react'
import { BackofficeLayout } from '../../components/layout/BackofficeLayout'
import { ordersApi } from '@eshop/shared'

interface OrderItem {
  id: number
  product: number
  vendor: number
  vendor_email: string
  vendor_business_name: string
  quantity: number
  unit_price: number
  product_name: string
  product_image_url: string | null
  subtotal: number
}

interface Order {
  id: number
  user: number
  customer_name: string
  customer_email: string
  customer_phone: string
  status: string
  total_amount: number
  currency: string
  shipping_address: string
  shipping_city: string
  shipping_postal_code: string
  shipping_country: string
  created_at: string
  items: OrderItem[]
}

export function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [_selectedOrder, _setSelectedOrder] = useState<Order | null>(null)

  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      // getMyOrders returns only orders with products from the logged-in vendor
      const data = await ordersApi.vendor.getMyOrders()
      setOrders(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar órdenes')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'Procesando', className: 'bg-blue-100 text-blue-800' },
      completed: { label: 'Completado', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-800' },
      refunded: { label: 'Reembolsado', className: 'bg-purple-100 text-purple-800' },
    }

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toString().includes(searchTerm)

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter

    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Cargando órdenes...</div>
        </div>
      </BackofficeLayout>
    )
  }

  if (error) {
    return (
      <BackofficeLayout>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </BackofficeLayout>
    )
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Mis Órdenes</h1>
            <p className="text-neutral-500 mt-1">Gestiona las órdenes de tus productos</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <div className="bg-white rounded-xl shadow-sm p-5 border border-neutral-200">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Total</p>
            <p className="text-2xl font-bold text-neutral-900 mt-1">{orders.length}</p>
          </div>
          <div className="bg-warning-50 rounded-xl shadow-sm p-5 border border-warning-200">
            <p className="text-xs font-medium text-warning-700 uppercase tracking-wide">Pendientes</p>
            <p className="text-2xl font-bold text-warning-600 mt-1">
              {orders.filter(o => o.status === 'pending').length}
            </p>
          </div>
          <div className="bg-blue-50 rounded-xl shadow-sm p-5 border border-blue-200">
            <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Procesando</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {orders.filter(o => o.status === 'processing').length}
            </p>
          </div>
          <div className="bg-success-50 rounded-xl shadow-sm p-5 border border-success-200">
            <p className="text-xs font-medium text-success-700 uppercase tracking-wide">Completadas</p>
            <p className="text-2xl font-bold text-success-600 mt-1">
              {orders.filter(o => o.status === 'completed').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-5">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por número de orden, cliente o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="processing">Procesando</option>
              <option value="completed">Completado</option>
              <option value="cancelled">Cancelado</option>
              <option value="refunded">Reembolsado</option>
            </select>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-5">
          {filteredOrders.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-8 text-center text-neutral-500">
              No se encontraron órdenes con tus productos
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div key={order.id} className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
                {/* Order Header */}
                <div className="bg-neutral-50 px-6 py-4 border-b border-neutral-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Orden #{order.id}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      {getStatusBadge(order.status)}
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Total de tus productos</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${order.items.reduce((sum, item) => sum + Number(item.subtotal), 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Details */}
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    {/* Customer Info */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Cliente</h4>
                      <p className="text-sm text-gray-900">{order.customer_name}</p>
                      <p className="text-sm text-gray-600">{order.customer_email}</p>
                      {order.customer_phone && (
                        <p className="text-sm text-gray-600">{order.customer_phone}</p>
                      )}
                    </div>

                    {/* Shipping Address */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Dirección de envío</h4>
                      <p className="text-sm text-gray-900">{order.shipping_address}</p>
                      <p className="text-sm text-gray-600">
                        {order.shipping_city}, {order.shipping_postal_code}
                      </p>
                      <p className="text-sm text-gray-600">{order.shipping_country}</p>
                    </div>
                  </div>

                  {/* Order Items (only vendor's items) */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Tus Productos en esta Orden</h4>
                    <div className="space-y-2">
                      {order.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            {item.product_image_url ? (
                              <img
                                src={item.product_image_url}
                                alt={item.product_name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                <span className="text-gray-400 text-xs">Sin img</span>
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.product_name}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">
                              ${Number(item.subtotal).toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-600">
                              {item.quantity}x ${Number(item.unit_price).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-blue-800">
            Mostrando {filteredOrders.length} de {orders.length} órdenes con tus productos
          </p>
        </div>
      </div>
    </BackofficeLayout>
  )
}
